import { IPromise } from 'angular';

import { IDetailFetcher } from '../fetchers/IDetailFetcher';
import { IListFetcher } from '../fetchers/IListFetcher';
import { RequestHelper } from '../helpers/RequestHelper';
import { ResultHelper } from '../helpers/ResultHelper';
import { ServerApiMapper } from '../helpers/ServerModelHelper';
import { WorkbookMapper } from '../mappers/WorkbookMapper';
import { WorkbookDefinition } from '../models/ContentDefinitions';
import { WorkbookContentType } from '../models/ContentTypes';
import { ITaggableContent } from '../models/ITaggableContent';
import { IWorkbookDetailInfo, IWorkbookInfo } from '../models/IWorkbookInfo';
import { Module } from '../Module';
import { AddTagsToWorkbooksRequest } from '../ServerApi/requests/AddTagsToWorkbooksRequest';
import { CancelExtractCreationRequest } from '../ServerApi/requests/CancelExtractCreationRequest';
import { CreateExtractTasksRequest } from '../ServerApi/requests/CreateExtractTasksRequest';
import { DeleteWorkbooksRequest } from '../ServerApi/requests/DeleteWorkbooksRequest';
import { GetFieldValuesRequest } from '../ServerApi/requests/GetFieldValuesRequest';
import { GetWorkbookActionsRequest } from '../ServerApi/requests/GetWorkbookActionsRequest';
import { GetWorkbooksRequest } from '../ServerApi/requests/GetWorkbooksRequest';
import {
  IIsWorkbookServerRefreshableRequest,
  IIsWorkbookServerRefreshableResult,
} from '../ServerApi/requests/IsWorkbookServerRefreshable';
import { MarkRecentlyViewedRequest, RecentlyViewedObjectType } from '../ServerApi/requests/MarkRecentlyViewedRequest';
import { MoveWorkbooksToProjectRequest } from '../ServerApi/requests/MoveWorkbooksToProjectRequest';
import { PerformExtractEncryptionActionOnWorkbooksRequest } from '../ServerApi/requests/PerformExtractEncryptionActionOnWorkbooksRequest';
import { RemoveTagsFromWorkbooksRequest } from '../ServerApi/requests/RemoveTagsFromWorkbooksRequest';
import { RenameWorkbookRequest } from '../ServerApi/requests/RenameWorkbookRequest';
import { IRevertWorkbookResult, RevertWorkbookRequest } from '../ServerApi/requests/RevertWorkbookRequest';
import { RunCreateExtractsOnWorkbooksRequest } from '../ServerApi/requests/RunCreateExtractsOnWorkbooksRequest';
import { RunExtractRefreshesOnWorkbooksRequest } from '../ServerApi/requests/RunExtractRefreshesOnWorkbooksRequest';
import { SetDisplayTabsRequest } from '../ServerApi/requests/SetDisplayTabsRequest';
import { SetWorkbookDescriptionRequest } from '../ServerApi/requests/SetWorkbookDescriptionRequest';
import { SetWorkbooksOwnerRequest } from '../ServerApi/requests/SetWorkbooksOwnerRequest';
import { UnencryptedConnectionKeychain } from '../ServerApi/types/IConnectionKeyChain';
import { ExtractTaskTargetType } from '../ServerApi/types/IExtractTask';
import { IFieldValue } from '../ServerApi/types/IFieldValue';
import { FieldValueResourceType } from '../ServerApi/types/IFieldValuesParams';
import { IResult } from '../ServerApi/types/IResponse';
import { IWorkbookActions } from '../ServerApi/types/IWorkbookActions';
import { ErrorNotifyingServer } from '../services/ErrorNotifyingServer';
import { FetcherFactory } from '../services/FetcherFactory';
import { ServerService } from '../services/ServerService';
import { FieldValuesQuery } from './FieldValuesQuery';
import { IActionResource, IDetailResource, IFieldValuesResource, IListResource } from './Resource';
import { ResourceQuery } from './ResourceQuery';
import { EnableWorkbookAccelerationRequest } from '../../dataAcceleration/serverApi/requests/EnableWorkbookAccelerationRequest';
import { DisableWorkbookAccelerationRequest } from '../../dataAcceleration/serverApi/requests/DisableWorkbookAccelerationRequest';
import { GetExtractDraftIdRequest } from '../ServerApi/requests/GetExtractDraftIdRequest';

export class Workbooks implements IListResource<IWorkbookInfo>, IFieldValuesResource, IDetailResource<IWorkbookDetailInfo>, IActionResource<IWorkbookActions>, ITaggableContent {

  public static $inject = [
    '$q',
    'ErrorNotifyingServer',
    'FetcherFactory'
  ];

  private server: ServerService;
  private listFetcher: IListFetcher<IWorkbookInfo>;
  private allQuery: ResourceQuery<IWorkbookInfo>;
  private detailFetcher: IDetailFetcher<IWorkbookDetailInfo>;
  private fieldValuesFetcher: IListFetcher<IFieldValue>;

  constructor(
    private $q: ng.IQService,
    private ErrorNotifyingServer: ErrorNotifyingServer,
    FetcherFactory: FetcherFactory
  ) {
    this.server = ErrorNotifyingServer.getServerService();
    this.listFetcher = FetcherFactory.getServerResourceFetcher(GetWorkbooksRequest, ServerApiMapper.workbooks);
    this.allQuery = new ResourceQuery(this.listFetcher);
    this.detailFetcher = FetcherFactory.getServerDetailFetcher('getWorkbook', WorkbookMapper.mapDetail);
    this.fieldValuesFetcher = FetcherFactory.getServerResourceFetcher(GetFieldValuesRequest, ServerApiMapper.fieldValues);
  }

  public static isWorkbookInfo(item: any): item is IWorkbookInfo {
    return item &&
           item.contentType &&
           item.contentType === WorkbookDefinition.contentType &&
           item.hasOwnProperty('numSheets'); // this field exists in IWorkbookInfo and not IWorkbookDetailInfo
  }

  public static isWorkbookDetailInfo(item: any): item is IWorkbookDetailInfo {
    return item &&
      item.contentType &&
      item.contentType === WorkbookDefinition.contentType &&
      item.hasOwnProperty('description'); // this field exists in IWorkbookDetailInfo and not IWorkbookInfo
  }

  public all(): ResourceQuery<IWorkbookInfo> {
    return this.allQuery;
  }

  public getById(id: string): IPromise<IWorkbookDetailInfo> {
    return this.detailFetcher.fetchById(id);
  }

  public valuesForField(field: string): FieldValuesQuery {
    return new FieldValuesQuery(this.fieldValuesFetcher, field, 'workbook');
  }

  // Actions

  public actions(id: string): IPromise<IWorkbookActions> {
    return this.server.sendRequest(new GetWorkbookActionsRequest(id));
  }

  public del(ids: string[]): IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) => new DeleteWorkbooksRequest(idsBlock)),
      ResultHelper.combineResults);
  }

  public rename(id: string, name: string): IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequestAndDelay(new RenameWorkbookRequest(id, name));
  }

  public setDescription(id: string, description: string): IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequest(new SetWorkbookDescriptionRequest(id, description));
  }

  public setOwner(ids: string[], ownerId: string): IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) => new SetWorkbooksOwnerRequest(idsBlock, ownerId)),
      ResultHelper.combineResults);
  }

  public moveToProject(targetProjectId: string, workbookIds: string[]): IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequestsAndDelay(
      RequestHelper.buildRequests(workbookIds,
        (idsBlock) => new MoveWorkbooksToProjectRequest({
          ids: idsBlock,
          projectId: targetProjectId
        })),
      ResultHelper.combineResults);
  }

  public setDisplayTabs(ids: string[], displayTabs: boolean): IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids,
        (idsBlock) => new SetDisplayTabsRequest({
          ids: idsBlock,
          displayTabs: displayTabs
        })),
      ResultHelper.combineResults);
  }

  public addTags(ids: string[], tags: string[]): IPromise<IResult> {
    return (tags.length > 0)
      ? this.ErrorNotifyingServer.sendRequestsAndDelay(
        RequestHelper.buildRequests2(ids, tags,
          (idsBlock, tagsBlock) => new AddTagsToWorkbooksRequest(idsBlock, tagsBlock)),
        ResultHelper.combineResults)
      : this.$q.when({});
  }

  public removeTags(ids: string[], tags: string[]): IPromise<IResult> {
    return (tags.length > 0)
      ? this.ErrorNotifyingServer.sendRequestsAndDelay(
        RequestHelper.buildRequests2(ids, tags,
          (idsBlock, tagsBlock) => new RemoveTagsFromWorkbooksRequest(idsBlock, tagsBlock)),
        ResultHelper.combineResults)
      : this.$q.when({});
  }

  public revertWorkbook(id: string, version: number, keychains?: UnencryptedConnectionKeychain[], encryptedPasswordKeyId?: string): IPromise<IRevertWorkbookResult> {
    const request = new RevertWorkbookRequest({
      id: id,
      version: version,
      keychains: keychains,
      encryptedPasswordKeyId: encryptedPasswordKeyId
    });
    return this.ErrorNotifyingServer.sendRequestAndDelay(request);
  }

  public changeTags(ids: string[], tagsToAdd: string[], tagsToRemove: string[]): IPromise<IResult> {
    return this.removeTags(ids, tagsToRemove)
      .then((result: IResult) => ResultHelper.hasErrors(result) ? result : this.addTags(ids, tagsToAdd));
  }

  public createExtractTask(ids: string[], scheduleId: string, extractType: string): IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequests(
      RequestHelper.buildRequests(ids,
        (idsBlock) => new CreateExtractTasksRequest({
          targetType: ExtractTaskTargetType.Workbook,
          targetIds: idsBlock,
          scheduleId: scheduleId,
          type: extractType // ExtractTaskType
        })),
      ResultHelper.combineResults);
  }

  public enableWorkbookAcceleration(ids: string[], scheduleId: string): IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequests(
      RequestHelper.buildRequests(ids,
        (idsBlock) => new EnableWorkbookAccelerationRequest({
          targetIds: idsBlock,
          scheduleId: scheduleId
        })),
      ResultHelper.combineResults);
  }

  public disableWorkbookAcceleration(ids: string[]): IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequests(
      RequestHelper.buildRequests(ids,
        (idsBlock) => new DisableWorkbookAccelerationRequest({
          targetIds: idsBlock
        })),
      ResultHelper.combineResults);
  }

  public refreshExtracts(ids: string[], extractType: string): IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequests(
      RequestHelper.buildRequests(ids, (idsBlock) =>
          new RunExtractRefreshesOnWorkbooksRequest(idsBlock, extractType)
      ),
      ResultHelper.combineResults);
  }

  public createExtracts(ids: string[], dsIds: string[], encrypt: boolean): IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) =>
      new RunCreateExtractsOnWorkbooksRequest(idsBlock, dsIds, encrypt)
      ),
      ResultHelper.combineResults);
  }

  public encryptWorkbooks(ids: string[], actionType: string): ng.IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) =>
        new PerformExtractEncryptionActionOnWorkbooksRequest(idsBlock, actionType)
      ),
      ResultHelper.combineResults);
  }

  public markRecentlyViewed(id: string) {
    return this.server.sendRequest(new MarkRecentlyViewedRequest({
      objectId: id,
      objectType: RecentlyViewedObjectType.Workbook
    }));
  }

  public isWorkbookServerRefreshable(id: string): IPromise<IIsWorkbookServerRefreshableResult> {
    return this.ErrorNotifyingServer.sendRequest(new IIsWorkbookServerRefreshableRequest({ id: id }));
  }

  public cancelExtractCreationOnWorkbooks(ids: string[]): ng.IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) =>
        new CancelExtractCreationRequest(ids, WorkbookContentType.name)
      ),
    ResultHelper.combineResults);
  }

  public getExtractDraftId(workbookId: string): ng.IPromise<IResult> {
    return this.server.sendRequest(new GetExtractDraftIdRequest(workbookId));
  }
}

Module.instance().service('Workbooks', Workbooks);
